/**
 * Created by MateuszWaligora on 12.07.2017.
 */
import Vue from 'vue';
import { HTTPCODES} from '../../plugins/types';
import _ from 'underscore';
import Product from '../../api/Product';

/**
 * @module Store/product
 * @description Module for products
 */

/**
 * @constant State of product store
 * @type {Object}
 * @property {Array} productGroups - product groups fetched from the server
 * @description This constant holds the state of product store
 * */
const state = {
    productGroups: [],
    products: [],
    defaultProducts: []
};

/**
 * @constant Getters
 * @type {Object}
 * @property {function} getProductGroups - returns productGroups array
 * @description This constant represents the getters for the product store
 * */
const getters = {
    getProductGroups: state => state.productGroups,
    getProducts: state => state.products,
    // getProductsById: state => state.products.find(el => el.id === id),
    getProductsById: state => id => state.products.filter(el => el.id === id),
    getDefaultProducts: state => state.defaultProducts,
    getDefaultProductById: state => id => state.defaultProducts.filter(el => el.id === id),
};

/**
 * @constant Mutations
 * @type {Object}
 * @property {function} updateProductGroupList - updates product groups list
 * @description This constant represents the mutations for the product store
 * */
const mutations = {
    updateProductGroupList: (state, payload) => state.productGroups = payload,
    updateProducts: (state, payload) => state.products = payload,
    updateDefaultProducts: (state, payload) => state.defaultProducts = payload
};
/**
 * @constant Actions
 * @type {Object}
 * @property {function} getProductGroupListAsync - async action that fetches the product groups from the server
 * @property {function} populateProductsInChosenGroupAsync - async action that fetches the products for chosen product group
 * @property {function} populatePricesAsync - async action that fetches the prices from the server
 * @description This constant represents the actions for the product store
 * */
const actions = {
    async addProductAsync ({rootState, dispatch, commit}, {product, price, disableFetch}) {
        let body = product;
        try {
            let {data} = await Vue.axios.post(`${rootState.config.productServicePort}/product/`, body, {
                headers: {
                    'Content-type': 'application/json',
                    'Store': window.localStorage.getItem('Store')
                }
            });
            await dispatch('addPriceAsync', {productId: data.id, price: price, disableFetch: disableFetch, source: 'addProductAsync'});
            if(!disableFetch) {
                dispatch('getProductListAsync');
                dispatch('getProductGroupListAsync');
                dispatch('getPricesAsync');
            }
            return data;
        } catch (err) {
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'add product'
            });
            throw new Error(err);
        }
    },
    async updateProductAsync ({rootState, dispatch, commit}, {product, productPrice, price, disableFetch}) {
        try {
            let res = await Vue.axios.put(`${rootState.config.productServicePort}/product/${product.id}`, product);
            if(productPrice.id) {
                await dispatch('updatePriceAsync', {price: {...productPrice, price: price}, disableFetch});
                return res;
            } else {
                await dispatch('addPriceAsync', {productId: product.id, price, disableFetch: disableFetch});
                commit('addAlert', {
                    title: 'Successfully ',
                    type: 'success',
                    body: `updated product with id ${res.id}`
                });
                return res;
            }
        } catch (err) {
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: `to update product: ${product.id}`
            });
            throw new Error(err);
        }
    },
    /**
     * @function getProductGroupListAsync
     * @description async action that fetches the product groups from the server
     * @returns {Object} product group list or error object

     * */
    async getProductGroupListAsync ({commit, rootState}) {
        try {
            let {data} = await Vue.axios.get(`${rootState.config.productServicePort}/product/group`);
            commit('updateProductGroupList', data || []);
            return {data};
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get product groups'
            });
            throw new Error(err);
        }
    },

    /**
     * @function getProductGroupListAsync
     * @description async action that fetches the product groups from the server
     * @returns {Object} product group list or error object

     * */
    async getProductGroupList ({commit, rootState}) {
        try {
            const product = new Product(rootState, rootState.context);
            const commitPayload = await product.groups();
            commit('updateProductGroupList', commitPayload);
            return commitPayload;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('updateProductGroupList', []);
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get product groups'
            });
            throw new Error(err);
        }
    },

    async loadProductsList ({rootState, commit}, shopid){
        try {
            let id = shopid || rootState.context;
            const product = new Product(rootState, id);
            const commitPayload = await product.findWithPrices();
            commit('updateProducts', commitPayload);
            return commitPayload;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('updateProducts', []);
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get product list'
            });
            throw new Error(err);
        }
    },

    async loadProducts ({ rootState }, shopid){
        try {
            let id = shopid || rootState.context;
            const product = new Product(rootState, id);
            const commitPayload = await product.findWithPrices();
            return commitPayload;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            throw new Error(err);
        }
    },
    async loadProduct ({ rootState }, shopid){
        try {
            let id = shopid || rootState.context;
            const product = new Product(rootState, id);
            await product.findWithPrices();
            return product;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            throw new Error(err);
        }
    },

    async getProductListAsync ({commit, rootState}) {
        try {
            let {data} = await Vue.axios.get(`${rootState.config.productServicePort}/product`);
            let commitPayload = data ? data.filter(item => item) : [];
            commit('updateProducts', commitPayload);
            return commitPayload;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to get product list'
            });
            throw new Error(err);
        }
    },

    async getProductDefaultListAsync ({commit, rootState}) {
        try {
            let id = 'default-config';
            const product = new Product(rootState, id);
            const commitPayload = await product.findWithPrices();
            commit('updateDefaultProducts', commitPayload);
            return commitPayload;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            throw new Error(err);
        }
    },
    async getProductDefaultAsync ({commit, rootState}) {
        try {
            let id = 'default-config';
            const product = new Product(rootState, id);
            await product.findWithPrices();
            return product;
        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            throw new Error(err);
        }
    },
    async setProductsBulkAsync ({commit, rootState, getters }, {shops, products, promotions}) {
        const newProducts = products.map(p => {
            return {
                ...p.toSave,
                price: p.priceValue
            }
        })
        try {
            const { data } = await Vue.axios.post(`${rootState.config.configurator}/productsbulk`, {shops, products: newProducts, promotions});
        
            commit('addAlert', {
                title: 'Success ',
                type: 'success',
                body: 'to update bulk shops for products, prices and promotions'
            });
            
            try {
                await Vue.axios.post('/collecter/classes/shopslogs', {products: products, shops: shops, promotions:promotions, userid: getters.getUserId, data: { type: "update-bulk", "success": true} } ,{headers: {'X-Parse-Application-Id':'uGJEQnHj5DajteynrhvzAXnMH8'}});
            } catch (error) {
                console.error(error)
            }

        } catch (err) {
            if(err.response && err.response.status >= 400) {
                if(err.response.status === 401) {
                    return {err: HTTPCODES[err.response.status]};
                }
            }
            commit('addAlert', {
                title: 'Failed ',
                type: 'danger',
                body: 'to update bulk shops for products, prices and promotions'
            });
            await Vue.axios.post('/collecter/classes/shopslogs', {products: products, shops: shops, promotions:promotions, userid: getters.getUserId, data: { type: "update-bulk", "success": false} } ,{headers: {'X-Parse-Application-Id':'uGJEQnHj5DajteynrhvzAXnMH8'}});
            throw new Error(err);
        }
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
